import type { ReactNode } from 'react';
import { NAV_SURFACE_MODULE, type Ability, type ModuleSlug } from '@/lib/permissions';

/**
 * A row whose destination is a URL this app LEARNS AT RUNTIME, outside itself.
 *
 * ⚠️ **The URL is not in this file, and cannot be.** This module is static data
 * evaluated at import; the Google Sheets mirror's address comes from the API,
 * derived from environment variables on the server, and is `null` on a box where
 * the mirror is switched off. So an external row names its SOURCE, and
 * `Sidebar.tsx` resolves that to an `href` at render — dropping the row entirely
 * when there is none. A row here is a claim that the destination exists, and for
 * these it does not always.
 *
 * A union of one today. It is a union rather than a boolean so the second one
 * cannot be added without naming where its URL comes from.
 */
export type NavExternalSource = 'client-deliverables-sheet';

interface NavItemBase {
  /**
   * What the row shows.
   *
   * ⚠️ *SUPERSEDED 2026-09-02: "Kept short so a 256px sidebar never wraps to two
   * lines."* The owner renamed all seven Dashboards and Trackers rows to their
   * full product names, so the longest is now `University Partnerships
   * Dashboard`. **It cannot WRAP** — the span is `truncate` in `Sidebar.tsx`, so
   * an over-long name ellipsises rather than growing the row — and the full text
   * is on the `title` and in the `sr-only` name either way. So length costs
   * legibility, never layout.
   *
   * ⚠️ **Length is irrelevant in the COLLAPSED RAIL**, which is the reading that
   * looks most at risk and is not: `Sidebar.tsx` renders the label span only
   * when expanded, so a collapsed row is its icon plus a tooltip.
   */
  label: string;
  /**
   * The full product name, when `label` is an abbreviation of it. This is what
   * assistive tech announces and what the tooltip shows, so shortening the
   * visible text never costs anyone the real name.
   *
   * ⚠️ **NO NAV ROW SETS THIS AS AN EXPANSION ANY MORE** — the seven that did
   * were renamed on 2026-09-02 so that `label` IS the full name, and the
   * duplicate was removed rather than left to drift. The field stays because
   * `Sidebar.tsx` and `GlobalSearch.tsx` both read `fullLabel ?? label` and a
   * future abbreviation is exactly what it is for. **Do not set it to a value
   * equal to `label`**: that is a second copy of one string with nothing keeping
   * the two in step.
   */
  fullLabel?: string;
  /**
   * A sub-heading inside the group (owner 2026-10-19): Product Insights rows
   * follow SEED's Attract · Nurture · Convert stages from
   * universities.seedglobaleducation.com. The sidebar prints the heading above
   * the first row of each run; rows with the same value must sit together.
   */
  stage?: string;
  /**
   * Hide the row entirely below the admin tier.
   *
   * For pages the API refuses outright — the audit log 403s anyone under
   * `admin` even to read. A row that always fails is worse than no row, and
   * unlike a write control there is nothing on the page to fall back to.
   * Pages that merely have admin-only CONTROLS must NOT set this: everyone can
   * read them, and hiding them would remove working screens.
   */
  adminOnly?: boolean;
  /**
   * Hide below `super_admin`.
   *
   * Stricter than the API, which lets any admin manage permission sets. Access
   * control is deliberately the narrowest surface in the app.
   */
  superAdminOnly?: boolean;
  /**
   * Hide the row unless the permission list allows reading this module.
   *
   * The third member of the same family as `adminOnly` and `superAdminOnly`
   * above — a declarative field on the item, evaluated in the ONE filter in
   * `Sidebar.tsx` — rather than a parallel mechanism. It differs only in
   * carrying a slug instead of a boolean, because the answer comes from
   * `ability.canRead()` rather than from a role.
   *
   * ⚠️ *SUPERSEDED 2026-09-04: "Set on the eight Dashboards and Trackers
   * surfaces, on the owner's instruction that those sections appear in
   * permission sets."* **It is on the MODULES rows too now** — Projects,
   * Products, Companies, Deals, Quotes, Invoices — and on Integrations under
   * Admin, on the owner's instruction that every module be gated after a
   * `viewer` on the `operations` set (which carries an explicit `contacts read
   * = none`) was found able to open Contacts. **Do not read a count out of this
   * sentence**; count the rows, which is the only place the answer lives.
   *
   * It is NOT the right field for a page the API refuses outright — that is
   * `adminOnly`, whose job is to drop a row that could only ever lead to a 403.
   * ⛔ **Never weaken an `adminOnly` row into a `module` one**: `adminOnly` is
   * the stricter answer, and `/audit-log` and `/settings` are admin tier on the
   * READ, so a permission could only ever offer a page whose every request 403s.
   * A row may carry both fields; none does, because none needs to.
   *
   * ⚠️ **Contacts deliberately has NO ROW to gate** — it is indexed on
   * `/catalog` — so `contacts` appears in no item here. Its page carries the
   * gate instead, which is where the reported bug actually lived.
   *
   * ⚠️ **This DEGRADES OPEN**: a module absent from the reader's permission
   * list is allowed, so an unseeded catalogue, an older API or a failed
   * `/permissions/me` leaves every row visible. Only an explicit `none` scope
   * hides one. See `NAV_SURFACE_MODULE` in `@/lib/permissions` for why.
   *
   * ⚠️ A routeless row keeps its inert "Soon" state when allowed — gating
   * decides only whether the row exists, never how it renders. Do not read a
   * hidden row as a reason to give a placeholder a `to`, and a placeholder that
   * ships gains its `to` in place rather than becoming a second row.
   *
   * 2026-10-02: an ARRAY means "readable if ANY of these is" — the Settings row,
   * whose page is now four modules (`settings` split into `settings-*`).
   */
  module?: ModuleSlug | readonly ModuleSlug[];
  /**
   * Draws a count badge beside the row: how many things on that page are
   * waiting for somebody. `Sidebar.tsx` resolves the key to a number — the row
   * names WHICH count, never the count itself, because the number is live data
   * and this file is static configuration.
   *
   * A union of one today, so a second count has to name where its number comes
   * from. Nothing renders when the count is `null`, `undefined` or `0` — an
   * unread count is not zero, and a zero is not news.
   */
  countKey?: 'enquiries';
  icon: ReactNode;
}

/** An in-app destination. `to` absent = an area that isn't built yet ("Soon"). */
interface NavRouteItem extends NavItemBase {
  to?: string;
  externalSource?: never;
}

/** A destination outside SEED EMERGE. See `NavExternalSource`. */
interface NavExternalItem extends NavItemBase {
  externalSource: NavExternalSource;
  to?: never;
}

/**
 * ⚠️ **A DISCRIMINATED UNION, AND THE POINT OF IT IS THE `never`s.**
 *
 * The obvious shape — one interface with an optional `href` beside `to` — lets a
 * URL be written into `to`, and `<NavLink to="https://…">` does not fail: it
 * builds a CLIENT-SIDE route to a path that does not exist, so the click lands
 * on the app's 404 with the URL mangled into the address bar. Nothing throws,
 * `tsc` is green, and it looks like a broken link rather than a wrong element.
 *
 * With `to?: never` on one arm and `externalSource?: never` on the other, a row
 * carrying both is a compile error and `Sidebar.tsx` can branch on
 * `externalSource` knowing an external row can never reach the `NavLink` path.
 */
export type NavItem = NavRouteItem | NavExternalItem;

export interface NavGroup {
  /** Rendered as the section label, and labels the list for assistive tech. */
  label: string;
  items: readonly NavItem[];
}

/**
 * May this reader be OFFERED this row?
 *
 * ⛔ **THE ONE COPY OF THIS RULE, AND IT MUST STAY ONE.** `Sidebar.tsx` and
 * `GlobalSearch.tsx` both decide who is offered which page, and until 2026-09-04
 * only the sidebar decided anything at all — the command palette flattened
 * `NAV_GROUPS` at module load, where there is no ability to consult, and handed
 * every reader a link to every page including ones their permission set denies.
 * Two copies of this rule would agree on the day they were written and drift on
 * the first correction, and **what would drift is who can see which page**.
 *
 * ⚠️ **A VIEW CONTROL, NOT ACCESS CONTROL.** The `NAV_SURFACE_MODULE`
 * surfaces have no server guard behind them — the API answers the same to
 * everybody — so this decides what is OFFERED and never what is permitted.
 * Nothing built on it may be worded as though the data were protected.
 *
 * 🔴 *SUPERSEDED 2026-10-01: "It DEGRADES OPEN on the module test … only an
 * explicit `none` scope denies."* A module row now needs `own` or `all` on its
 * `read` — an absent row hides it. Before `/permissions/me` loads (or if it
 * fails) ordinary reads still degrade open, and the privileged screens (audit
 * log, settings, API keys, support sessions, permission sets) stay hidden.
 * `adminOnly` survives only on the rows with no module behind them.
 *
 * The external-destination test is deliberately NOT here: it needs an href
 * resolved at render from a runtime read, which is the sidebar's own business.
 * Callers `&&` it on.
 */
export function isNavItemOffered(item: NavItem, ability: Ability): boolean {
  return (
    (!item.adminOnly || ability.isAdmin) &&
    (!item.superAdminOnly || ability.isSuperAdmin) &&
    (!item.module ||
      (typeof item.module === 'string'
        ? ability.canRead(item.module)
        : item.module.some((slug) => ability.canRead(slug))))
  );
}

/*
 * Spread onto each icon's <svg> rather than wrapped in an <Icon> component:
 * this module exports data only, and a component here would break fast refresh.
 */
const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  className: 'h-4 w-4 shrink-0',
} as const;

/*
 * Three sections in the order the business reads them: the dashboards opened in
 * the morning, the trackers worked through during the day, then the entities all
 * of that is built out of. Admin is last because it is configuration, not work.
 *
 * Most rows have no route yet and so carry no `to`, which is what renders the
 * inert "Soon" treatment. That is deliberate here — the shape of the product is
 * the point of this nav, not only what happens to be built.
 *
 * Every item carries a distinct icon: the collapsed rail shows nothing else, so
 * two similar glyphs at 16px are two unidentifiable rows.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'Dashboards',
    items: [
      /*
       * ⚠️ **`Overview` → `/dashboard` was REMOVED, and it is not coming back
       * as a placeholder row.** The page rendered invented figures — a whole
       * dashboard of plausible numbers over a near-empty database — and the
       * owner had it deleted rather than left on screen. A routeless "Soon" row
       * in its place would promise the same page again; when a real overview is
       * built it gets a new row, on the same rule every other row here follows.
       *
       * This row was also the post-login landing route. That is now
       * `/university-partnerships` below, resolved per-user through
       * `use-landing-page.ts` — so nothing depends on a row existing here.
       */
      {
        /*
         * ⚠️ **RENAMED 2026-09-02 ON THE OWNER'S INSTRUCTION, AND THE RENAME WAS
         * OF `label`, NOT OF `fullLabel`.** `label` is the text the sidebar
         * paints; `fullLabel` is the tooltip, the accessible name and the
         * GlobalSearch entry, and it already read "University Partnerships
         * Dashboard". The owner asked for the RENDERED text to become the full
         * name, so the two collapsed into one string and the redundant
         * `fullLabel` went — `Sidebar.tsx` reads `item.fullLabel ?? item.label`,
         * so an omitted one costs nothing and a duplicated one is a second copy
         * that drifts.
         */
        label: 'University Partnerships Dashboard',
        /*
         * Gained its `to` in place when the page shipped, rather than being
         * duplicated into a second entry — the same move `/projects` made. A
         * placeholder that ships keeps its row; a new row beside it is how a
         * nav ends up with two entries for one thing.
         */
        to: '/university-partnerships',
        module: NAV_SURFACE_MODULE.dashboardUniversityPartnerships,
        icon: (
          <svg {...iconProps}>
            <path d="M21.4 10.9a1 1 0 0 0 0-1.84L12.83 5.2a2 2 0 0 0-1.66 0L2.6 9.06a1 1 0 0 0 0 1.84l8.57 3.9a2 2 0 0 0 1.66 0Z" />
            <path d="M22 10v6" />
            <path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5" />
          </svg>
        ),
      },
      {
        /* Renamed 2026-09-02 with its siblings — see the row above. */
        label: 'Management Dashboard',
        /*
         * ⚠️ **This row GAINED its `to` in place when the dashboard shipped
         * (2026-09-02)** — it did not become a second entry beside the
         * placeholder, which is how a nav ends up with two rows for one thing.
         * Same move `/university-partnerships`, `/accounts-dashboard`,
         * `/projects`, `/settings`, `Client Deliverables` and `Invoice` all
         * made.
         *
         * ⚠️ **`/management`, deliberately not `/management-dashboard`.** The
         * `/accounts-dashboard` suffix exists to stop that path colliding with a
         * list of companies, which this CRM calls accounts; there is no
         * `/management` record list to collide with, so the suffix would be
         * noise. Both are addresses somebody types once and bookmarks.
         */
        to: '/management',
        module: NAV_SURFACE_MODULE.dashboardManagement,
        icon: (
          <svg {...iconProps}>
            <rect x="9" y="2" width="6" height="6" rx="1.5" />
            <rect x="2" y="16" width="6" height="6" rx="1.5" />
            <rect x="16" y="16" width="6" height="6" rx="1.5" />
            <path d="M5 16v-2.5a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1V16" />
            <path d="M12 12.5V8" />
          </svg>
        ),
      },
      /* A pie chart: the one dashboard here whose subject is a split of money. */
      {
        /* Renamed 2026-09-02 with its siblings — see University Partnerships. */
        label: 'Accounts Dashboard',
        /*
         * ⚠️ This row GAINED its `to` in place when the dashboard shipped — it
         * did not become a second entry beside the placeholder, which is how a
         * nav ends up with two rows for one thing. Same move
         * `/university-partnerships`, `/projects`, `/settings`, `Client
         * Deliverables` and `Invoice` all made.
         *
         * ⚠️ The path is `/accounts-dashboard` and NOT `/accounts`, which reads
         * as the obvious short form and would be the wrong word here: in this
         * CRM every COMPANY is an account, so `/accounts` promises a list of
         * companies. This page is quotes, invoices and planned documents.
         */
        to: '/accounts-dashboard',
        module: NAV_SURFACE_MODULE.dashboardAccounts,
        icon: (
          <svg {...iconProps}>
            <path d="M12 2.5V12h9.5a9.5 9.5 0 0 0-9.5-9.5Z" />
            <path d="M20.9 15.6A9.5 9.5 0 1 1 8.4 3.1" />
          </svg>
        ),
      },
      /*
       * A gauge — a half arc with a needle. Every other glyph in this group is a
       * FULL circle or a box (a mortarboard, an org chart, a pie), and the
       * collapsed rail shows nothing but the icon, so a fourth ring would be a
       * fourth unidentifiable row.
       *
       * ⚠️ **A FOURTH DESTINATION, NOT A REPLACEMENT.** This page shows the
       * Client Deliverables Tracker, the Future Billables Tracker and Companies
       * behind pills on one screen; all three keep their own rows, on this
       * codebase's standing rule that two destinations get two rows. Somebody
       * who wants one tracker on its own still has a way to it, and its address
       * is still a link that can be sent.
       *
       * ⛔ **Their ROUTES must not be removed** whatever happens to their rows.
       * Deep links into `/client-deliverables`, `/future-billables-tracker` and
       * `/companies` are bookmarked, sent in messages and written into saved
       * filters; taking the routes away turns every one of them into this app's
       * 404, and nothing anywhere reports it.
       */
      {
        label: 'Operations Dashboard',
        to: '/operations',
        /*
         * ⚠️ **A VIEW control, never access control** — like every other row
         * here. The pages behind the pills answer the same to everybody, so
         * hiding this row hides a way IN and protects no data.
         *
         * ⚠️ It DEGRADES OPEN, which is what makes it safe to ship before the
         * slug is seeded server-side: an absent module reads as allowed, so the
         * row is visible to everyone until the migration lands rather than
         * disappearing for everyone. `NAV_SURFACE_MODULE` in `@/lib/permissions`
         * carries the reasoning, and the case where that trade does NOT hold.
         */
        module: NAV_SURFACE_MODULE.dashboardOperations,
        icon: (
          <svg {...iconProps}>
            <path d="M2.5 17.5a9.5 9.5 0 0 1 19 0" />
            <path d="m12 17.5 3.5-6" />
            <circle cx="12" cy="17.5" r="1.25" />
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Trackers',
    items: [
      /*
       * Not gated on a ROLE, and that has not changed: the tasks and notes
       * routers carry no role guard at all — a viewer may create both — and the
       * page is the caller's own queue, so there is no tier it belongs above.
       *
       * ⚠️ It now carries a `module`, which this comment used to rule out. The
       * owner asked for all eight Dashboards and Trackers surfaces to appear in
       * permission sets, this row among them. The two statements coexist: the
       * server still refuses nobody, and an administrator may now hide the row.
       * Hiding it is a VIEW decision and never access control — `/tasks` stays
       * reachable by URL and every endpoint behind it stays open.
       *
       * ⚠️ `NAV_SURFACE_MODULE.trackerMyTasks` is NOT the `tasks` permission
       * module. `tasks` gates the records and is read by the drawers all over
       * the app; this gates one sidebar row. Do not collapse them — denying
       * `tasks.read` to hide a nav row would blank the activity drawer on every
       * company, deal, quote and invoice in the product.
       */
      {
        /*
         * ⚠️ **RENAMED 2026-09-02, and this row LOST MORE THAN THE OTHER SIX.**
         * Its `fullLabel` was `My tasks and day brief` — a DESCRIPTION, not an
         * expansion of an abbreviation, so it was the tooltip and the accessible
         * name while the rail read "My Tasks". The owner named this row "Tasks
         * Tracker", and a row whose visible text and whose announced name are two
         * different sentences is one that a sighted reader and a screen-reader
         * reader cannot discuss with each other. So the description went with the
         * rename rather than being kept beside it.
         *
         * ⚠️ **"Tasks Tracker", not "My Tasks Tracker" — the page is still the
         * CALLER'S OWN queue** and nothing about that changed. The scope is said
         * on the page, which is where it can be said properly.
         */
        label: 'Tasks Tracker',
        to: '/tasks',
        module: NAV_SURFACE_MODULE.trackerMyTasks,
        icon: (
          <svg {...iconProps}>
            <path d="M9 5.5h9.5" />
            <path d="M9 12h9.5" />
            <path d="M9 18.5h9.5" />
            <path d="m3.5 5.5 1.5 1.5 2.5-3" />
            <path d="m3.5 12 1.5 1.5 2.5-3" />
            <circle cx="5" cy="18.5" r="1.5" />
          </svg>
        ),
      },
      /*
       * The caller's own notification store (`/notifications`). No `module`:
       * every signed-in person owns their preferences and inbox.
       */
      {
        label: 'Notifications',
        to: '/notifications',
        icon: (
          <svg {...iconProps}>
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
        ),
      },
      /*
       * ⚠️ This row GAINED its `to` in place when the tracker shipped — it did
       * not become a second entry beside the placeholder, which is how a nav
       * ends up with two rows for one thing.
       *
       * ⚠️ It used to sit beside a `Deliverables` row under Modules, and that
       * row is now GONE — the old hand-entered entity was deleted, not merged
       * into this one. The two were always different resources rather than two
       * views of one: this is a derived tracker over quote and invoice line
       * items, grouped by project; that was a list of records somebody typed.
       * Read this row as the survivor of a deletion, never as a rename, or the
       * next person will go looking for the entity list this replaced.
       */
      {
        /* Renamed 2026-09-02 with its siblings — see University Partnerships. */
        label: 'Client Deliverables Tracker',
        to: '/client-deliverables',
        module: NAV_SURFACE_MODULE.trackerClientDeliverables,
        icon: (
          <svg {...iconProps}>
            <rect x="8" y="2" width="8" height="4" rx="1" />
            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
            <path d="m9 14 2 2 4-4" />
          </svg>
        ),
      },
      /*
       * ⚠️ **`Tracker in Sheets` — the Google Sheets mirror — was REMOVED from
       * this nav on the owner's instruction (2026-09-02), and the external-row
       * machinery it was the only user of is DELIBERATELY LEFT STANDING.**
       *
       * `NavExternalSource`, the `NavExternalItem` arm of `NavItem` below, and
       * `Sidebar.tsx`'s `externalHrefs` / `hrefFor` / anchor branch now have no
       * call site. They encode a decision rather than a convenience: an
       * external destination must never reach `<NavLink>`, which does not fail
       * on an absolute URL — it routes to a path that does not exist and lands
       * the reader on this app's own 404. The `never`s on that union are what
       * enforce it, so deleting them makes the next external row a rebuild.
       *
       * ⚠️ **Half-dismantling it would be worse than either keeping it or
       * removing it whole.** `Sidebar.tsx` is untouched for that reason: its
       * `useTrackerSheetLink()` call is one cached read per tab, shared with the
       * page below, so it costs essentially nothing — whereas stubbing that
       * `Record` to a `null` href would leave a re-added row compiling,
       * rendering NOTHING, and saying nothing about why.
       *
       * ⚠️ **The CAPABILITY did not go with the row.** `OpenTrackerSheetButton`
       * on `/client-deliverables` still opens the mirror and is still the
       * hook's other caller — so do not read this removal as the mirror being
       * switched off, and do not "restore" it by rebuilding that button.
       */
      /*
       * ⚠️ **THIS IS THE `Invoice Tracker` ROW'S SLOT, AND IT CAME BACK AS A
       * DIFFERENT ROW — 2026-09-04, owner's decision.**
       *
       * *SUPERSEDED, the ~30-line note that stood here: "🔴 THE `Invoice
       * Tracker` ROW WAS HERE AND IS GONE — owner's instruction, 2026-09-02,
       * 'for now' … 🔴 **THE PAGE IS NOT DELETED AND MUST NOT BE.**
       * `/invoice-tracker` is still a live route in `App.tsx` … this is a row
       * waiting to come back, not a feature being retired … ⚠️
       * **`trackers-invoice` STILL EXISTS AS A PERMISSION AND NOW GATES NOTHING
       * VISIBLE.**"*
       *
       * 🔴 **Every one of those three claims is now false, and each was
       * falsified by the same change**: `/invoice-tracker` and
       * `InvoiceTrackerPage.tsx` are DELETED, the feature WAS retired rather
       * than paused, and `trackers-invoice` gates a visible row again — the one
       * below. The owner was shown, and accepted, that the old page's Raised
       * view and its quote- and invoice-attached rows go with it.
       *
       * ⚠️ **The nav's own rule held and is worth reading twice**: a row that
       * comes back comes back HERE, in this position, rather than as a second
       * entry beside a comment describing the first. What changed is the label
       * and the destination — not the slot, and not the module.
       */
      {
        /*
         * ⚠️ **THE MODULE SLUG BEHIND THIS ROW IS STILL `trackers-invoice`, AND
         * IT DELIBERATELY DOES NOT MOVE.** The row is named for future
         * billables; the slug is migration 108's and is a JOIN KEY that resolves
         * the seeded permission sets, so renaming it would disconnect this
         * surface from every grant already made against it — silently, because
         * an unknown slug degrades open rather than failing.
         *
         * A backend change is renaming the module's DISPLAY NAME to "Future
         * Billables Tracker" so an administrator sees the same words this row
         * shows. **That is the label only.** The grid renders the name off
         * `GET /permissions`, so no client change is needed for it and none
         * should be made — and nothing in this app may ever spell a display name
         * as a slug. Fifth instance of the write-ids / render-names /
         * match-on-neither rule in this codebase.
         *
         * ⚠️ **This is a VIEW control and never access control.** Unticking it
         * hides this row and makes the page say so; `GET /invoice-tracker`
         * answers the same to everybody, and `/future-billables-tracker` stays
         * reachable by URL — which is why the page carries its own refusal
         * panel rather than relying on the row being absent.
         *
         * ⚠️ It is NOT a second row for `Invoices` under Modules. That row is
         * the invoice RECORDS, a list you create and edit documents from. This
         * is the money nobody has billed yet — rows that name no document at
         * all. Two questions, two pages.
         *
         * ⚠️ Nor is it a second address for the Accounts Dashboard's Future
         * Billables section, which reads the identical row set and STAYS. That
         * section is one band of a finance dashboard, read beside quotes and
         * invoices; this is the tracker over the whole population, with its own
         * filters, paging and export.
         */
        label: 'Future Billables Tracker',
        to: '/future-billables-tracker',
        module: NAV_SURFACE_MODULE.trackerInvoice,
        icon: (
          <svg {...iconProps}>
            <rect x="3" y="5" width="18" height="16" rx="2" />
            <path d="M3 10h18" />
            <path d="M8 3v4" />
            <path d="M16 3v4" />
            <path d="M9 15.5h6" />
            <path d="m12.5 13 2.5 2.5-2.5 2.5" />
          </svg>
        ),
      },
      /*
       * ⚠️ This row GAINED its `to` in place when the tracker shipped — it did
       * not become a second entry beside the placeholder, which is how a nav
       * ends up with two rows for one thing. Same move `/projects`,
       * `/settings` and `Client Deliverables` above all made.
       *
       * ⚠️ *SUPERSEDED 2026-09-04, the third name in that list and the sentence
       * below it: "`Invoice` above" and "It is NOT a second address for the
       * Invoice Tracker above. That page is one row per DOCUMENT, scoped to a
       * financial year."* **`/invoice-tracker` was retired** and its slot now
       * holds the Future Billables Tracker.
       *
       * ⚠️ The distinction still holds against its replacement, and is why they
       * are two rows: that page is one row per PLANNED document. This is a
       * CROSS-TAB — one row per COMPANY, banded by billing region, with a group
       * of four columns per cycle. Two questions, two pages.
       */
      {
        /*
         * Renamed 2026-09-02 with its siblings — see University Partnerships.
         *
         * ⚠️ MY RENAME SAID "still ROUTELESS: it carries no `to` and renders the
         * inert Soon treatment", and that was FALSE BY THE TIME IT LANDED. A
         * parallel session built `/revenue-tracker` in the same hours, and the
         * two commits met in a rebase — theirs adding the route, mine asserting
         * there was none.
         *
         * ⚠️ IT IS THE WORKED EXAMPLE OF THIS FILE'S OWN RULE, arriving from the
         * direction the rule does not warn about: "a placeholder that ships
         * GAINS ITS `to` IN PLACE; it never becomes a second entry." Theirs did
         * exactly that. What the rule does not say is that the sentence calling
         * a row a placeholder is falsified by the same edit — and a claim about
         * the ABSENCE of a feature is the one nobody re-reads, because the
         * person who builds it is never the person holding that sentence.
         *
         * `fullLabel` is gone here as on the other six: `label` is what renders
         * and `fullLabel` was the tooltip, so two copies of one string is one
         * that can drift.
         */
        label: 'Revenue Tracker',
        to: '/revenue-tracker',
        module: NAV_SURFACE_MODULE.trackerRevenue,
        icon: (
          <svg {...iconProps}>
            <circle cx="12" cy="12" r="9.5" />
            <path d="M15.5 8.5H10a2 2 0 0 0 0 4h4a2 2 0 0 1 0 4H8.5" />
            <path d="M12 6.5v1.5" />
            <path d="M12 16.5V18" />
          </svg>
        ),
      },
      {
        /*
         * ⚠️ **`sales-tracker` IS A BARE SLUG HERE AND NOT A `NAV_SURFACE_MODULE`
         * KEY, and the difference is the whole point of the row.** The four
         * trackers above gate view controls this app alone enforces — no
         * endpoint stands behind them. This one names a REAL module with a real
         * server guard, the same class as `tasks`, `notes` and `targets`: the
         * API refuses a write this set does not grant, whatever the sidebar
         * shows. Same shape as the `Projects` row below, which spells its module
         * the same way and for the same reason.
         *
         * ⚠️ **Deliberately NOT `adminOnly`.** The row's tier follows the
         * endpoint's READ guard, and migration 164 grants read to every set —
         * full CRUD to `university-partnerships` and `management`, read-only to
         * `accounts` and `operations`. `adminOnly` would hide the board from a
         * whole TIER, where this hides it only from a set that has written
         * `Denied` against `sales-tracker`. That is the mistake `Settings` made
         * once from the other direction: "surely anyone may look at this" is the
         * reasoning to distrust, in both directions.
         *
         * ⚠️ Hiding the row does NOT close the page: `/sales-tracker` stays
         * reachable by URL, which is why the page renders its own refusal panel
         * rather than relying on this row being absent.
         *
         * ⚠️ It is NOT a second address for the board on `/deals`. That one is
         * one card per DEAL, moving through pipeline stages. This is one lane per
         * PROJECT, one card per company being pursued for it, counted against the
         * project's capacity. Two questions, two pages.
         */
        label: 'Sales Tracker',
        to: '/sales-tracker',
        module: 'sales-tracker',
        icon: (
          // A lane of cards under a target: two columns, one of them filled, over
          // a baseline. Drawn for this row rather than borrowed from another —
          // people find a row by its shape, and two rows sharing a glyph is a
          // sidebar somebody has to read.
          <svg {...iconProps}>
            <path d="M3 20h18" />
            <rect x="4" y="12" width="6" height="5" rx="1" />
            <rect x="14" y="8" width="6" height="9" rx="1" />
            <path d="M4 4.5h16" />
            <path d="M7 4.5v3" />
            <path d="M17 4.5v2" />
          </svg>
        ),
      },
      /*
       * What institutions told the public 2027 festival interest form: the
       * submissions, and the demand report that answers "who wants Mumbai".
       *
       * ⚠️ **A NEW ROW, NOT A PLACEHOLDER GAINING ITS `to`.** There has never
       * been a routeless `Event Interest` entry in this group — checked against
       * the whole file before adding this one, because a second row for one
       * thing is exactly how this nav has gone wrong before.
       *
       * ⛔ **`event-interest` IS A BARE SLUG AND NOT A `NAV_SURFACE_MODULE` KEY,
       * AND THIS IS THE GROUP WHERE THAT GOES WRONG.** Most of the rows above it
       * — Tasks, Client Deliverables, Future Billables, Revenue — ARE nav
       * surfaces: view controls with no endpoint behind them, which is why they
       * spell their module through that object. So the obvious move when adding
       * another Tracker is a `trackerEventInterest` key beside theirs, and it
       * would be wrong twice over. That set forces `create`/`update`/`delete` to
       * a hard `false`, and it files a module among the view controls where
       * **hiding a page is explicitly NOT refusing a request** — while this one
       * has a real server guard and real records behind it. `Sales Tracker`
       * directly above already spells its module this way, for this reason.
       *
       * ⚠️ **Deliberately NOT `adminOnly`.** A row's tier follows the endpoint's
       * READ guard, and `event-interest.routes.ts` carries no role guard at all
       * (measured 2026-09-17 — `requireRole` appears in it only inside
       * comments). The staff reads are decided by `event-interest.read` through
       * the permission sets, which that router says are granted to the staff
       * sets "because staff are who the module is for". Hiding the row below
       * admin would withhold a page the server serves them — the mistake
       * `Settings` made once from the other direction.
       *
       * 🔴 **THE GRANTS ARE UNVERIFIED: THE PERMISSIONS MIGRATION IS NOT WRITTEN
       * YET.** Nothing in `migrations/` catalogues this module (measured
       * 2026-09-17), so it is absent from `/permissions/me` — and module gating
       * DEGRADES OPEN, so today this row is visible to everybody rather than
       * hidden from anybody. ⛔ **If that migration grants `event-interest.read`
       * to the administrator sets ALONE, this row needs `adminOnly` beside the
       * slug**, the way the two Commissions rows carry both: the slug fails open
       * and would leave the row showing to exactly the people it was meant to
       * keep out. Re-read the migration when it lands rather than this sentence.
       *
       * ⚠️ Hiding the row does NOT close the page: `/event-interest` carries no
       * route guard, which is why the page renders its own refusal panel rather
       * than relying on this row being absent.
       *
       * ⚠️ It is NOT a second address for the Client Deliverables Tracker above.
       * That one is work already SOLD, derived from quote and invoice lines.
       * This is interest EXPRESSED on a public form by institutions who may have
       * bought nothing — and, on a company that has never been approved. Two
       * questions, two pages.
       *
       * A map pin: the report's whole question is which CITIES institutions
       * want, and nothing else in the rail is a pin. The `Brand` droplet is the
       * nearest silhouette, and it is two groups away, points the other way up,
       * and has no interior circle — which is the part that reads at 16px, where
       * the collapsed rail shows nothing but the icon.
       */
      {
        label: 'Enquiries Tracker',
        to: '/event-interest',
        module: 'event-interest',
        countKey: 'enquiries',
        icon: (
          <svg {...iconProps}>
            <path d="M12 21s7-6.1 7-11a7 7 0 1 0-14 0c0 4.9 7 11 7 11Z" />
            <circle cx="12" cy="10" r="2.5" />
          </svg>
        ),
      },
      /*
       * Prospect Follow-ups (owner 2026-10-23). A bare slug, like Sales Tracker:
       * `trackers-prospect-follow-ups` is catalogued by migration 312 and the
       * endpoint behind it is guarded by `companies.read`. Hiding the row does
       * not close the page; it renders its own refusal.
       */
      {
        label: 'Prospect Follow-ups',
        to: '/prospect-follow-ups',
        module: 'trackers-prospect-follow-ups',
        icon: (
          // An alarm clock (Lucide `alarm-clock`): contact due within N days.
          <svg {...iconProps}>
            <circle cx="12" cy="13" r="8" />
            <path d="M12 9v4l2 2" />
            <path d="M5 3 2 6" />
            <path d="m22 6-3-3" />
            <path d="M6.38 18.7 4 21" />
            <path d="M17.64 18.67 20 21" />
          </svg>
        ),
      },
      /*
       * Email Log (owner 2026-10-23): BCC-logged threads and the unmatched
       * triage queue. A real server-guarded module (`email-log.read`/`triage`,
       * migration 312), so a bare slug.
       */
      {
        label: 'Email Log',
        to: '/email-log',
        module: 'email-log',
        icon: (
          // An envelope with an arrow in (Lucide `mail-plus` silhouette, arrow
          // in place of the plus): mail arriving into the log. The plain
          // envelope is already Email Accounts' and Email Settings' glyph.
          <svg {...iconProps}>
            <path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            <path d="M19 16v6" />
            <path d="m16 19 3 3 3-3" />
          </svg>
        ),
      },
    ],
  },
  {
    /*
     * ⚠️ The label is the STORAGE KEY for whether the section is open —
     * `emerge.sidebar.openGroups` holds labels, and a label the nav no longer
     * has is dropped on read. So renaming this label silently collapses the
     * section once for anyone who had it open — self-correcting, but worth
     * knowing before renaming a group people keep open.
     */
    label: 'Modules',
    items: [
      /*
       * A project is a record you manage, not a tracker view — this row lives
       * under Modules, not Trackers. If a Project TRACKER DASHBOARD is ever
       * built, it needs its own row here rather than reusing this one.
       *
       * Deliberately NOT `adminOnly`. Reads are open to every authenticated
       * role — a viewer gets the real list — and only create/update/delete are
       * gated, on the page itself.
       *
       * ⚠️ The `module` added 2026-09-04 does not contradict that: `adminOnly`
       * would hide the row from a whole TIER, where this hides it only from a
       * set that has written `Denied` against `projects`. The API still answers
       * every role, so this is a view control and the page says so itself.
       */
      {
        label: 'Projects',
        to: '/projects',
        module: 'projects',
        icon: (
          <svg {...iconProps}>
            <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
            <path d="M8 10v4" />
            <path d="M12 10v2" />
            <path d="M16 10v6" />
          </svg>
        ),
      },
      /*
       * ⚠️ `products` is a module whose WRITES are admin-only however generous
       * the set is (`ADMIN_WRITE_MODULES`), and whose READS are open to every
       * role. So the row is gated on the module and not on the tier: hiding it
       * from every non-admin would remove a page a viewer can legitimately read,
       * which is the `/users` mistake this nav has already been warned about.
       */
      {
        label: 'Products',
        to: '/products',
        module: 'products',
        icon: (
          <svg {...iconProps}>
            <path d="M3 8.5V4a1 1 0 0 1 1-1h4.5a2 2 0 0 1 1.42.59l9.5 9.5a2 2 0 0 1 0 2.82l-4.5 4.5a2 2 0 0 1-2.82 0l-9.5-9.5A2 2 0 0 1 3 8.5Z" />
            <circle cx="7.5" cy="7.5" r="1.25" />
          </svg>
        ),
      },
      {
        label: 'Companies',
        to: '/companies',
        module: 'companies',
        icon: (
          <svg {...iconProps}>
            <path d="M3 21h18" />
            <path d="M5 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16" />
            <path d="M15 21V9h4a2 2 0 0 1 2 2v10" />
            <path d="M9 7h2M9 11h2M9 15h2" />
          </svg>
        ),
      },
      /*
       * Contacts has NO row here, deliberately — it is indexed on /catalog
       * instead, the same way cycles and intakes are. `/contacts` and
       * `/contacts/:id` are still real routes and a real page; this is a
       * navigation decision, not a removal. Companies keeps its row, so the
       * way to a contact in practice is either the catalog or the company it
       * belongs to.
       */
      /* A briefcase: the piece of work being negotiated, not a document. */
      {
        label: 'Deals',
        to: '/deals',
        module: 'deals',
        icon: (
          <svg {...iconProps}>
            <rect x="2.5" y="7" width="19" height="13" rx="2" />
            <path d="M8.5 7V5a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v2" />
            <path d="M2.5 12.5h19" />
          </svg>
        ),
      },
      {
        label: 'Quotes',
        to: '/quotes',
        module: 'quotes',
        icon: (
          <svg {...iconProps}>
            <path d="M6 2.5h7.5L19 8v11.5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-15a2 2 0 0 1 2-2Z" />
            <path d="M13.5 2.5V8H19" />
            <path d="M7.5 12.5h6" />
            <path d="M7.5 16.5h3.5" />
          </svg>
        ),
      },
      /*
       * A banknote rather than a third piece of paper: the Invoice Tracker above
       * is already a receipt and Quotes is already a page, and three paper
       * silhouettes are indistinguishable in the 16px rail.
       */
      {
        label: 'Invoices',
        to: '/invoices',
        module: 'invoices',
        icon: (
          <svg {...iconProps}>
            <rect x="2" y="6" width="20" height="12" rx="2" />
            <circle cx="12" cy="12" r="2.5" />
            <path d="M6 10v4" />
            <path d="M18 10v4" />
          </svg>
        ),
      },
      /*
       * ⚠️ **A NEW ROW, NOT A PLACEHOLDER GAINING ITS `to`.** There has never
       * been a routeless `Credit Notes` entry here — checked before adding this
       * one, because a second row for one thing is exactly how this nav has gone
       * wrong before. Every other Modules row that shipped gained its `to` in
       * place; this one had no placeholder to gain it from.
       *
       * ⚠️ **Modules, beside Invoices — not Trackers and not Dashboards.** A
       * credit note is a revenue DOCUMENT somebody raises, edits and deletes,
       * the same class as a quote or an invoice. Trackers are readings OVER
       * documents (the same rows re-cut by project, by cycle, by what has not
       * been billed yet), and a dashboard is a reading over those. Filing it by
       * what it is about rather than by what it is would put a record list
       * behind a heading that promises a view.
       *
       * ⚠️ **`credit-notes` is a BARE SLUG and NOT a `NAV_SURFACE_MODULE` KEY** —
       * the same standing as `Sales Tracker` and `Projects`, and for the same
       * reason. Migration 200 catalogues it with five permissions and the API
       * enforces the permission set rather than the role, so this is a REAL
       * module with a real server guard. ⛔ `NAV_SURFACE_MODULE` forces
       * `create`/`update`/`delete` to a hard `false` because a dashboard has
       * nothing to change; folding this in there would delete the very
       * affordances the module exists to gate.
       *
       * ⚠️ **Deliberately NOT `adminOnly`.** A row's tier follows the endpoint's
       * READ guard, and migration 200 grants `read` to every seeded set —
       * operations, university-partnerships, accounts, management, super-admin.
       * Hiding the row below admin would withhold a page the server serves them,
       * which is the mistake `Settings` made once from the other direction:
       * "surely only administrators need this" is the reasoning to distrust, in
       * both directions.
       *
       * ⚠️ Hiding the row does NOT close the page: `/credit-notes` stays
       * reachable by URL and carries no route guard, which is why the page
       * renders its own refusal panel rather than relying on this row's absence.
       *
       * A card with an arrow leaving it to the left: money going back out. The
       * silhouette deliberately echoes the banknote above — they are the two
       * sides of one transaction and sit next to each other — while the interior
       * is an arrow rather than a coin, because the collapsed rail shows nothing
       * but the icon and two cards with the same middle are two unidentifiable
       * rows.
       */
      {
        label: 'Credit Notes',
        to: '/credit-notes',
        module: 'credit-notes',
        icon: (
          <svg {...iconProps}>
            <rect x="2" y="5" width="20" height="14" rx="2" />
            <path d="M16 12H8" />
            <path d="m11 8.5-3.5 3.5 3.5 3.5" />
          </svg>
        ),
      },
      /*
       * ⚠️ **`Deliverables` → `/deliverables` was REMOVED.** The old
       * hand-entered entity is deleted — page, detail page and form — on the
       * owner's ruling that `Client Deliverables` under Trackers replaces it.
       * The table held zero rows, so no record became unreachable.
       *
       * ⚠️ **The two rows were deliberately separate and only ONE of them
       * went.** The comment on the Trackers row above still says they are two
       * different resources, and it is still true — what changed is that this
       * app no longer offers a screen for the second one. Do not read the
       * surviving row as the old entity having been renamed into Trackers.
       */
    ],
  },
  /*
   * Product Insights — owner's ruling 2026-10-18. Live readings from SEED's
   * other products, starting with SEED FAB (the scholarship/admissions funnel).
   * `product-insights` is a real server guard (backend migration 289): read to
   * `management` and `university-partnerships`, `none` elsewhere. ⚠️ The group
   * LABEL is the storage key for `emerge.sidebar.openGroups`.
   */
  {
    label: 'Product Insights',
    items: [
      {
        label: 'Events',
        stage: 'Attract',
        to: '/product-insights/events',
        module: 'product-insights',
        icon: (
          // A calendar (Lucide `calendar`).
          <svg {...iconProps}>
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4" />
            <path d="M8 2v4" />
            <path d="M3 10h18" />
          </svg>
        ),
      },
      {
        label: 'School Profiles',
        stage: 'Attract',
        to: '/product-insights/school-profiles',
        module: 'product-insights',
        icon: (
          // A school building (Lucide `school`): Course Finder profile pages.
          <svg {...iconProps}>
            <path d="M14 22v-4a2 2 0 1 0-4 0v4" />
            <path d="m18 10 4 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-8l4-2" />
            <path d="M18 5v17" />
            <path d="m4 6 8-4 8 4" />
            <path d="M6 5v17" />
            <circle cx="12" cy="9" r="2" />
          </svg>
        ),
      },
      {
        label: 'Challenge Hub',
        stage: 'Attract',
        to: '/product-insights/challenges',
        module: 'product-insights',
        icon: (
          // A trophy (Lucide `trophy`): SEED Hackathon and Business Challenge.
          <svg {...iconProps}>
            <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
            <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
            <path d="M4 22h16" />
            <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
            <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
            <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
          </svg>
        ),
      },
      {
        label: 'Digital Advertising',
        stage: 'Attract',
        module: 'product-insights',
        // No route yet: the sidebar renders the row's inert "Soon" state (owner 2026-10-19).
        icon: (
          // A megaphone (Lucide `megaphone`): email, WhatsApp and social campaigns.
          <svg {...iconProps}>
            <path d="m3 11 18-5v12L3 14v-3z" />
            <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
          </svg>
        ),
      },
      {
        label: 'In-Country Representation',
        stage: 'Nurture',
        to: '/product-insights/icr',
        module: 'product-insights',
        icon: (
          // A globe (Lucide `globe`): representatives in other countries.
          <svg {...iconProps}>
            <circle cx="12" cy="12" r="10" />
            <path d="M2 12h20" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
        ),
      },
      {
        label: 'Lead & Application Engagement',
        stage: 'Nurture',
        to: '/product-insights/lae',
        module: 'product-insights',
        icon: (
          // A phone — the same path as `PhoneIcon` in components/catalog/icons.
          <svg {...iconProps}>
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" />
          </svg>
        ),
      },
      {
        label: 'Funnel Analytics & BI',
        stage: 'Convert',
        to: '/product-insights/fab',
        module: 'product-insights',
        icon: (
          // A funnel-shaped bar chart: three bars narrowing left to right.
          <svg {...iconProps}>
            <path d="M3 3v16a2 2 0 0 0 2 2h16" />
            <path d="M8 17V7" />
            <path d="M13 17v-6" />
            <path d="M18 17v-3" />
          </svg>
        ),
      },
      {
        label: 'Scholarships',
        stage: 'Convert',
        to: '/product-insights/scholarships',
        module: 'product-insights',
        icon: (
          // A graduation cap (Lucide `graduation-cap`): SEED FAB scholarship applications.
          <svg {...iconProps}>
            <path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z" />
            <path d="M22 10v6" />
            <path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5" />
          </svg>
        ),
      },
      {
        label: 'AI Visa Tutor',
        stage: 'Convert',
        to: '/product-insights/visa-tutor',
        module: 'product-insights',
        icon: (
          // A speech bubble (Lucide `message-square`): mock visa interviews.
          <svg {...iconProps}>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        ),
      },
      {
        label: 'Edumate',
        stage: 'Convert',
        module: 'product-insights',
        // No route yet: the sidebar renders the row's inert "Soon" state (owner 2026-10-19).
        icon: (
          // A wallet (Lucide `wallet`): financial support for admitted students.
          <svg {...iconProps}>
            <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" />
            <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />
          </svg>
        ),
      },
    ],
  },
  /*
   * The Marketing section — backend migration 256, `docs/api/marketing.md`.
   * Each row is gated on its own module, which is a real server guard granted
   * to the two administrator sets only. ⚠️ The group LABEL is the storage key
   * for `emerge.sidebar.openGroups`, so renaming it is a data change.
   */
  {
    label: 'B2B Marketing',
    items: [
      {
        label: 'Campaigns',
        to: '/marketing/campaigns',
        module: 'marketing-campaigns',
        icon: (
          <svg {...iconProps}>
            <path d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1Z" />
            <path d="M15 9a4 4 0 0 1 0 6" />
            <path d="M18 6.5a8 8 0 0 1 0 11" />
          </svg>
        ),
      },
      {
        label: 'Lists',
        to: '/marketing/lists',
        module: 'marketing-lists',
        icon: (
          <svg {...iconProps}>
            <path d="M9 6h11" />
            <path d="M9 12h11" />
            <path d="M9 18h11" />
            <circle cx="4.5" cy="6" r="1" />
            <circle cx="4.5" cy="12" r="1" />
            <circle cx="4.5" cy="18" r="1" />
          </svg>
        ),
      },
      {
        label: 'Templates',
        to: '/marketing/templates',
        module: 'marketing-templates',
        icon: (
          <svg {...iconProps}>
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 9h18" />
            <path d="M9 9v12" />
          </svg>
        ),
      },
      {
        label: 'Email Accounts',
        to: '/marketing/email-accounts',
        module: 'marketing-email-accounts',
        icon: (
          <svg {...iconProps}>
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m3 7 9 6 9-6" />
          </svg>
        ),
      },
      {
        label: 'B2B Marketing Settings',
        to: '/marketing/settings',
        module: 'marketing-settings',
        icon: (
          <svg {...iconProps}>
            <path d="M4 6h10" />
            <path d="M18 6h2" />
            <circle cx="16" cy="6" r="2" />
            <path d="M4 12h2" />
            <path d="M10 12h10" />
            <circle cx="8" cy="12" r="2" />
            <path d="M4 18h10" />
            <path d="M18 18h2" />
            <circle cx="16" cy="18" r="2" />
          </svg>
        ),
      },
    ],
  },
  /*
   * Not in the requested structure either. /users is a built, working page that
   * would otherwise be unreachable; Settings sits beside it as the other
   * configuration surface.
   */
  {
    label: 'Admin',
    items: [
      {
        label: 'Users',
        to: '/users',
        icon: (
          <svg {...iconProps}>
            <circle cx="12" cy="7.5" r="3.5" />
            <path d="M5 20.5a7 7 0 0 1 14 0" />
          </svg>
        ),
      },
      {
        label: 'Permission Sets',
        fullLabel: 'Permission sets and assignments',
        to: '/permission-sets',
        // 2026-10-01: the `permission-sets` module; only the super-admin set
        // holds it today. *SUPERSEDED: `superAdminOnly: true`.*
        module: 'permission-sets',
        icon: (
          <svg {...iconProps}>
            <rect x="4" y="10.5" width="16" height="10" rx="2" />
            <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
            <circle cx="12" cy="15.5" r="1.25" />
          </svg>
        ),
      },
      /*
       * The keys that let another SYSTEM call this API — today the public
       * event-interest form, whose server posts submissions with one. Filed
       * beside `Permission Sets` because both answer "who may do what", and
       * because they are the two narrowest rows in the rail.
       *
       * ⚠️ **A NEW ROW, NOT A PLACEHOLDER GAINING ITS `to`** — there has never
       * been a routeless `API Keys` entry here; checked before adding it.
       *
       * 🔴 **`superAdminOnly` FOR A DIFFERENT REASON THAN THE ROW ABOVE, AND THE
       * DIFFERENCE DECIDES WHETHER IT MAY EVER BE RELAXED.** `Permission Sets`
       * is deliberately STRICTER THAN THE API: any admin may write a permission
       * set, and this app narrows that by choice. This row MATCHES the API —
       * every route in `api-keys.routes.ts` carries
       * `requireRole(...SUPER_ADMIN_ROLES)` (measured 2026-09-17), so below that
       * tier every request here 403s. ⛔ **So it is not a house preference and
       * must never be "relaxed" to `adminOnly` on the grounds that the sibling
       * row is merely cautious**: that would be the `/audit-log` mistake one tier
       * up, a row leading an administrator to a page whose every request refuses
       * them, with nothing on the page able to explain it.
       *
       * ⚠️ **No `module`, deliberately.** There is no `api-keys` permission
       * module in the catalogue and this app must not invent one: a slug is a
       * join key against a catalogue in somebody else's database, and an
       * invented one resolves to nothing — which DEGRADES OPEN, handing the row
       * back to everybody it exists to keep out. `Users` and `Audit Log` sit here
       * without a slug for the same reason: the role is the whole answer.
       *
       * ⛔ **A KEY'S PLAINTEXT TOKEN IS RETURNED ONCE, AT CREATION, AND IS NEVER
       * STORED** — only its hash and a short prefix, which is what the list shows
       * to identify a key. Nothing behind this row can ever show a token again,
       * so no control under it may offer to, and "revoke" is a timestamp rather
       * than a delete.
       *
       * A key: the bow, the shaft and two teeth. Related to the padlock above it
       * and a different silhouette, which is what the collapsed rail needs —
       * it shows nothing but the icon.
       */
      {
        label: 'API Keys',
        fullLabel: 'API keys for other systems',
        to: '/api-keys',
        // 2026-10-01: the `api-keys` module now exists (the comment above saying
        // there is none is SUPERSEDED). *SUPERSEDED: `superAdminOnly: true`.*
        module: 'api-keys',
        icon: (
          <svg {...iconProps}>
            <circle cx="7.5" cy="15.5" r="3.75" />
            <path d="m10.2 12.8 8.3-8.3" />
            <path d="m15 8 2.2 2.2" />
            <path d="m17.6 5.4 2.2 2.2" />
          </svg>
        ),
      },
      /*
       * What the public 2027 interest form's keys resolve to — `event_key` → a
       * project, `city_key` → a city. Filed here rather than beside
       * `Event Interest` in Modules because it is CONFIGURATION somebody sets up
       * once, not a tracker anybody reads; the row above it issues the key that
       * form authenticates with, which is the other half of the same setup.
       *
       * ⚠️ **A NEW ROW, NOT A PLACEHOLDER GAINING ITS `to`** — there has never
       * been a routeless `Event Maps` entry here; checked before adding it.
       *
       * 🔴 **`adminOnly`, AND IT MATCHES THE API RATHER THAN NARROWING IT.**
       * Every route on `/event-maps` carries `requireRole(...ADMIN_ROLES)` **on
       * the READ as well as the writes**, so below that tier every request here
       * 403s and the only possible page is a refusal. That is the `Audit Log`
       * and `Settings` shape — a row dropped because it could only ever lead to
       * a 403 — and NOT the `Integrations` one, where a real screen exists
       * underneath for everybody and only the buttons are gated.
       *
       * ⚠️ **No `module`, deliberately.** There is no `event-maps` permission
       * module in the catalogue and this app must not invent one: a slug is a
       * join key against a catalogue in somebody else's database, and an
       * invented one resolves to nothing — which DEGRADES OPEN, handing the row
       * back to everybody it exists to keep out. ⛔ **`event-interest` is NOT
       * the slug to reach for either**: that module gates a tracker whose router
       * carries no role guard at all, so borrowing it here would offer the row
       * to a tier the server refuses outright. `Users` and `Audit Log` sit here
       * without a slug for the same reason — the role is the whole answer.
       *
       * 🔴 **AN UNMAPPED KEY IS A PUBLIC FORM REFUSING SUBMISSIONS**, not a gap
       * in a report: a key resolving to nothing is a 422 at submission time. So
       * this row leads to the only screen in SEED EMERGE whose emptiness stops
       * something outside it working.
       *
       * A folded map: three panels and two creases. Related to nothing else in
       * the rail, which is what the collapsed state needs — it shows the icon
       * and nothing else.
       */
      {
        label: 'Event Maps',
        fullLabel: 'What the 2027 form’s keys resolve to',
        to: '/event-maps',
        // 2026-10-02: `settings-maps`. *SUPERSEDED 2026-10-01: `settings`;
        // before that `adminOnly: true`, and "no `module`, deliberately" above.*
        module: 'settings-maps',
        icon: (
          <svg {...iconProps}>
            <path d="M9 4.5 4 6.5v13l5-2 6 2 5-2v-13l-5 2Z" />
            <path d="M9 4.5v13" />
            <path d="M15 6.5v13" />
          </svg>
        ),
      },
      /*
       * What a B2B proposal's service keys resolve to — the sender's own product
       * slug → a SEED EMERGE product. Filed beside `Event Maps` because it is the
       * same JOB: configuration somebody sets up once so a public form's
       * vocabulary means something here, not a tracker anybody reads.
       *
       * ⚠️ **A NEW ROW, NOT A PLACEHOLDER GAINING ITS `to`** — there has never
       * been a routeless `Service Maps` entry here; checked before adding it.
       *
       * 🔴 **`adminOnly`, AND IT CARRIES NO `module`.** There is no
       * `b2b-service-maps` permission module in the catalogue and this app must
       * not invent one: a slug is a join key against a catalogue in somebody
       * else's database, and an invented one resolves to nothing — which DEGRADES
       * OPEN, handing the row back to everybody it exists to keep out. `Users`,
       * `Audit Log` and `Event Maps` sit here without a slug for the same reason:
       * the role is the whole answer.
       *
       * 🔴 **AND IT IS NOT THE SAME STAKES AS THE ROW ABOVE IT, WHICH IS WORTH
       * KNOWING BEFORE ANYONE BORROWS ITS URGENCY.** An unmapped festival key is
       * a public form REFUSING a submission. An unmapped service key refuses
       * nothing — the proposal arrives, the key is kept verbatim, and it simply
       * reads as a raw slug until somebody maps it. So this row leads to a screen
       * whose emptiness costs LEGIBILITY, never an enquiry, and no copy behind it
       * may say otherwise.
       *
       * A luggage tag: a pierced corner and its eyelet. Related to nothing else in
       * the rail, which is what the collapsed state needs — it shows the icon and
       * nothing else.
       */
      {
        label: 'Service Maps',
        fullLabel: 'What a B2B proposal’s services resolve to',
        to: '/b2b-service-maps',
        // 2026-10-02: `settings-maps`. *SUPERSEDED 2026-10-01: `settings`;
        // before that `adminOnly: true`, and "no `module`" above.*
        module: 'settings-maps',
        icon: (
          <svg {...iconProps}>
            <path d="M3.5 12.5V4.5h8l9 9-8 8-9-9Z" />
            <circle cx="8" cy="8" r="1.25" />
          </svg>
        ),
      },
      /*
       * ⛔ **THE ONLY ROWS IN THIS RAIL THAT LEAD TO PAY.** Owner decision 24 is
       * administrators only — no sales lead sees a commission figure in EMERGE,
       * not even their own.
       *
       * 🔴 **THE FIRST ROWS HERE TO CARRY BOTH `adminOnly` AND `module`, and the
       * doc on `module` above anticipates exactly this ("a row may carry both
       * fields; none does, because none needs to"). These need both, and the
       * reason is that the two fail in OPPOSITE directions.**
       *
       * `module` **DEGRADES OPEN** — a module absent from the reader's
       * permission list is ALLOWED, so an unseeded catalogue, an API older than
       * migration 205, or a `/permissions/me` that simply failed leaves the row
       * visible to everybody. On every other gated row that is a harmless
       * over-offer into a page the reader can read anyway. Here it would put a
       * route to everybody's pay in a sales lead's sidebar, and the only thing
       * stopping them is a 403 they would first have to go and collect.
       *
       * `adminOnly` fails CLOSED and is the answer to that. It is also correct
       * on its own terms — migration 205 grants `commissions` to `management`
       * and `super-admin` alone, so below the admin tier every request here
       * 403s, which is precisely the "could only ever lead to a 403" case that
       * field exists for.
       *
       * ⚠️ **AND `module` IS NOT REDUNDANT BESIDE IT.** A super administrator
       * may revoke `commissions` from the `management` set — that is a real
       * configuration and the whole point of the module being permission-gated
       * rather than role-gated — and without the slug the row would go on
       * showing to an administrator whose grant had been taken away.
       *
       * ⛔ **DO NOT ADD `commissions` TO `NAV_SURFACE_MODULE` in
       * `@/lib/permissions`.** That set forces create, update and delete to
       * false, which would delete every affordance the module exists to gate —
       * and worse, it would file a server-enforced module among the nine view
       * controls, where hiding a page is explicitly NOT refusing a request.
       */
      {
        label: 'Commissions',
        fullLabel: 'Commission schemes and assignments',
        to: '/commissions',
        // 2026-10-01: `adminOnly` removed — the set decides, and an absent
        // module no longer degrades open (see the comment above, SUPERSEDED).
        module: 'commissions',
        icon: (
          <svg {...iconProps}>
            <circle cx="7.5" cy="7.5" r="2.5" />
            <circle cx="16.5" cy="16.5" r="2.5" />
            <path d="M18.5 5.5 5.5 18.5" />
          </svg>
        ),
      },
      {
        label: 'Commission Runs',
        fullLabel: 'Annual commission runs and statements',
        to: '/commission-runs',
        module: 'commissions',
        icon: (
          <svg {...iconProps}>
            <path d="M4 4.5A1.5 1.5 0 0 1 5.5 3h13A1.5 1.5 0 0 1 20 4.5v15a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 19.5Z" />
            <path d="M8 8h8" />
            <path d="M8 12h3" />
            <path d="m13.5 15.5 2 2 3.5-3.5" />
          </svg>
        ),
      },
      {
        label: 'Audit Log',
        fullLabel: 'API request audit log',
        to: '/audit-log',
        // 2026-10-01: *SUPERSEDED: `adminOnly: true`.* The set decides.
        module: 'api-logs',
        icon: (
          <svg {...iconProps}>
            <path d="M4 4.5A1.5 1.5 0 0 1 5.5 3H14l6 6v11.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 20.5Z" />
            <path d="M14 3v6h6" />
            <path d="M8.5 13.5h7" />
            <path d="M8.5 17h4.5" />
          </svg>
        ),
      },
      /*
       * The notification send log (`f_notification_deliveries`, kept forever)
       * and the branded email previews. Both read `settings-notifications`.
       */
      {
        label: 'Notification log',
        fullLabel: 'Notification send log',
        to: '/notification-log',
        module: 'settings-notifications',
        icon: (
          <svg {...iconProps}>
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
        ),
      },
      {
        label: 'Email templates',
        fullLabel: 'Notification email templates',
        to: '/notification-templates',
        module: 'settings-notifications',
        icon: (
          <svg {...iconProps}>
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m3 7 9 6 9-6" />
          </svg>
        ),
      },
      /*
       * A plug pulled out of its socket: the only row here about a system
       * outside SEED EMERGE, and the one glyph in the rail that is not a
       * document, a person or a shape.
       *
       * Deliberately NOT `adminOnly`. Unlike the audit log above, nothing has
       * established that the API refuses a lower role outright — and the page
       * gates its connect and disconnect controls itself, so there is a real
       * screen underneath them for everyone else to read.
       *
       * ⚠️ **`integrations` is the one module here whose `update` is a REAL
       * capability**, not an inert row: it connects and disconnects Xero. So
       * gating this row on `read` hides the screen and changes nothing about
       * who may press those buttons — the page's own `canUpdate` gate still
       * decides that, and it is untouched. Two questions, two checks.
       */
      {
        label: 'Integrations',
        fullLabel: 'Integrations with other systems',
        to: '/integrations',
        module: 'integrations',
        icon: (
          <svg {...iconProps}>
            <path d="M12 22v-5" />
            <path d="M9 8V2" />
            <path d="M15 8V2" />
            <path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z" />
          </svg>
        ),
      },
      {
        label: 'Settings',
        // Shipped 2026-08-29. It gains a `to` IN PLACE — a placeholder that
        // ships never becomes a second entry.
        //
        // ⚠️ `adminOnly` because `GET /settings/smtp` is admin tier on the READ,
        // not only on the test send. This row was first written as deliberately
        // NOT `adminOnly`, on the reading that every role may see whether email
        // is configured — the `/integrations` shape. The endpoint as built 403s
        // below admin even to look, which is the `/audit-log` shape, so the row
        // is hidden rather than leading every `basic_user` to a card whose only
        // possible answer to them is a refusal.
        //
        // 2026-10-01: *SUPERSEDED, the `adminOnly` above.* The set decides.
        // 2026-10-02: the page is four modules now, so the row shows when ANY
        // of them is readable.
        module: [
          'settings',
          'settings-documents',
          'settings-email',
          'settings-notifications',
        ],
        to: '/settings',
        icon: (
          <svg {...iconProps}>
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
          </svg>
        ),
      },
      /*
       * The mark, the palette, the type and the rules for using them.
       *
       * ⚠️ **Deliberately NOT `adminOnly`, and it is the odd row in this group.**
       * Everything else under Admin is configuration; this is reference material,
       * and the people most likely to want it — anyone putting the mark on a deck
       * or a document — are the least likely to be administrators. The group is
       * already visible below admin (`Users` and `Integrations` carry no gating),
       * so an open row here costs nothing and hides nothing.
       *
       * The page reads no API at all, so there is no endpoint whose guard this
       * row could follow. That is why the tier rule stated on `Settings` above
       * does not decide anything here.
       *
       * A droplet: the only glyph in the rail about colour, and distinct from
       * the person, padlock, document, plug and gear it sits beside — which is
       * what the collapsed rail needs, since it shows nothing but the icon.
       */
      {
        label: 'Brand',
        fullLabel: 'Brand mark and guidelines',
        to: '/brand',
        icon: (
          <svg {...iconProps}>
            <path d="M12 3s5.5 6.2 5.5 9.5a5.5 5.5 0 0 1-11 0C6.5 9.2 12 3 12 3Z" />
          </svg>
        ),
      },
    ],
  },
];

/*
 * Rendered beneath the groups as plain rows rather than as a section: this is a
 * single navigable page, and a collapsible group of one is a control that does
 * nothing. The page behind it is where the pickers and other masters live —
 * cycles and intakes among them, which is why neither has a row of its own
 * above any more. Contacts is indexed there too, under its own heading rather
 * than among the masters, because it is a CRM entity and not a picklist.
 *
 * A stack of layers, because that is what the page is: everything else, filed.
 */
export const NAV_STANDALONE: readonly NavItem[] = [
  {
    label: 'Modules Catalog',
    fullLabel: 'Additional Modules Catalog',
    to: '/catalog',
    icon: (
      <svg {...iconProps}>
        <path d="m12 2.5 9.5 5-9.5 5-9.5-5 9.5-5Z" />
        <path d="m2.5 12 9.5 5 9.5-5" />
        <path d="m2.5 16.5 9.5 5 9.5-5" />
      </svg>
    ),
  },
];
